import Deferred from "csi/_base/Deferred";
import html from "csi/html";
import csi from "csi/main";
import { ImageDimensions, SMALL_SIZE } from "csi/model/image";
import { RemoveEventHandle } from "csi/typings";
import has from "dojo/has";
import on from "dojo/on";

const ERROR_IMAGE = csi.image("ImageError.png");

/** list of deferreds for images being created */
let _imageDeferreds: Deferred[] = [];

/** mapping of image src to (width,height) pair */
const _imageSizes: Record<string, ImageDimensions> = {};

/**
 * Return size of given image if previously loaded
 */
export function getImageSize(img: HTMLImageElement): ImageDimensions {
	return _imageSizes[img.src] || { height: SMALL_SIZE, width: SMALL_SIZE };
}

/**
 * Return true if given image element encountered error while loading
 */
export function isErrorImage(img: Error | HTMLImageElement): boolean {
	return !img || (img instanceof Error) || img.src.includes(ERROR_IMAGE);
}

/**
 * Preload image with given src
 */
export function preloadImage(src: string): void {
	if (src && !_imageSizes[src]) {
		renderImage(null, src);
	}
}

/**
 * Render image under given refNode, returning deferred object that will be called when image is done
 * @param img - use given image node if provided
 * @param predictedHeight - predicted height of image
 * @param predictedWidth - predicted width of image
 */
export function renderImage(refNode: HTMLElement, src: string, img?: HTMLImageElement, className?: string, predictedHeight?: number, predictedWidth?: number): Deferred {
	const dfd = new Deferred();
	let handles: RemoveEventHandle[] = [];
	let span: HTMLElement;

	function onLoad(): void {
		// store size of image to catch browser errors where width/height is sometimes 0 on subsequent loads
		const data = _imageSizes[img.src];
		if (!data) {
			if ((img.width <= 0) && (img.height <= 0)) {
				// if zero dimension image, then error out
				img.src = ERROR_IMAGE;
			} else {
				const { height, width } = img;
				_imageSizes[img.src] = { height, width };
			}
		}

		handles?.forEach((h) => {
			h.remove();
		});

		// apply CSS after size has been determined
		if (className) {
			img.classList.add(className);
		}

		// if span was created as a placeholder for image, then now replace it with the image;
		// NOTE: span.parentNode may not be the same as "refNode" at this point; in table code,
		// the span can be wrapped within another node, so make sure to use the span's current parent
		span?.parentNode?.replaceChild(img, span);

		dfd.endTimer(true);
		dfd.resolve(img);
	}

	function onError(): void {
		if (img.src == ERROR_IMAGE) {
			handles?.forEach((h) => {
				h.remove();
			});

			dfd.endTimer(true);
			dfd.reject(new Error("csi/image::create - no image"));
		} else {
			img.src = ERROR_IMAGE;
		}
	}

	// FIX: if predicted height is known, then try just creating image node directly and settings its height and width
	// https://www.smashingmagazine.com/2020/03/setting-height-width-images-important-again/

	// create placeholder for image
	if (refNode && (!img || !img.parentNode)) {
		span = html.create("span", {
			class: className || "",
			style: "display: inline-block",
		}, refNode);

		if (predictedHeight) {
			html.setPixels(span, html.HEIGHT, predictedHeight);
		}

		if (predictedWidth) {
			html.setPixels(span, html.WIDTH, predictedWidth);
		}
	}

	// create image if not passed in
	img ||= new Image();

	handles = [
		on(img, "load", onLoad),
		on(img, "error", onError),
		on(img, "abort", onError),
	];

	if (src) {
		// if access to files is denied, then show error
		if (has("csi-no-files")) {
			src = ERROR_IMAGE;
		}

		// set source last to make sure the event functions get called
		dfd.startTimer();
		img.src = src;

		// track deferred so others can be notified when they are loaded
		_imageDeferreds.push(dfd);
	} else {
		dfd.reject(new Error("csi/image::create - no src"));
	}

	return dfd;
}

/**
 * Return promise that will be resolved when all outstanding images have been loaded;
 * but first, remove any deferreds already resolved to keep list short
 */
export function waitForImagesToLoad(): Promise<Deferred[]> {
	_imageDeferreds = _imageDeferreds.filter((dfd) => !dfd.isResolved());
	return Promise.all(_imageDeferreds);
}
